import "./Pricing.css";
import { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Alert, Modal, Button } from "antd";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../context/AppContextProvider";
import {
  getAllAddons,
  getSubscriptionPlansAsObject,
} from "../../network/network";
import { AddonName } from "../../configs/constants";
import CurrencyFormatter from "../CurrencyFormatter";
import QuantityStepper from "../QuantityStepper";
import {
  calculatePricingTotal,
  expandAddonDependencies,
  extractApiCollection,
  getNetPrice,
  getPlanUserNetPrice,
  sumAddonPrices,
  uniqueAddonsById,
} from "./pricingCalculations";
import { handleApiError } from "../../utils/errorHandler";
import { getDailySubscriptionPlan } from "../../utils/subscriptionPlans";

const GROUP_CONFIG = [
  {
    key: "fnb",
    addonNames: [
      AddonName.OrderManagement,
      AddonName.WaiterManagement,
      AddonName.QrOrdering,
      AddonName.TableManagement,
      AddonName.KitchenDisplaySystem,
      AddonName.CustomerDisplaySystem,
    ],
    colorClass: "brand",
    icon: "🍽️",
    titleKey: "pricing_group_fnb_title",
    descKey: "pricing_group_fnb_desc",
    conflictKey: "job",
    popupKey: "fnb",
  },
  {
    key: "job",
    addonNames: [
      AddonName.JobManagement,
      AddonName.OrderManagement,
      AddonName.CustomerManagement,
    ],
    colorClass: "green",
    icon: "🔧",
    titleKey: "pricing_group_job_title",
    descKey: "pricing_group_job_desc",
    conflictKey: "fnb",
    popupKey: "job",
  },
  {
    key: "stock",
    addonNames: [AddonName.Stock],
    colorClass: "gold",
    icon: "📦",
    titleKey: "pricing_group_stock_title",
    descKey: "pricing_group_stock_desc",
    conflictKey: null,
    popupKey: "stock",
  },
  {
    key: "ticket",
    addonNames: [AddonName.Ticketing],
    colorClass: "blue",
    icon: "🎫",
    titleKey: "pricing_group_ticket_title",
    descKey: "pricing_group_ticket_desc",
    conflictKey: null,
    popupKey: null,
  },
];

const POS_INCLUDED_ADDON_NAMES = [
  AddonName.CustomerDisplaySystem,
  AddonName.TapToPay,
];

const popupIndustryKeyMap = {
  pos: [
    "industry_gold_jewelry",
    "industry_clothing_fashion",
    "industry_pharmacies",
    "industry_grocery_supermarkets",
    "industry_cosmetics_beauty",
    "industry_electronics_phones",
    "industry_furniture_home",
    "industry_laundry_dry_clean",
    "industry_salons_barbershops",
    "industry_any_retail_business",
  ],
  fnb: [
    "industry_cafes_coffee_shops",
    "industry_dine_in_restaurants",
    "industry_fast_food_qsr",
    "industry_food_courts",
    "industry_bakeries_desserts",
    "industry_healthy_food_outlets",
    "industry_drive_through",
    "industry_cloud_kitchens",
  ],
  job: [
    "industry_ac_hvac_repair",
    "industry_car_wash_detailing",
    "industry_electrical_services",
    "industry_plumbing_maintenance",
    "industry_cleaning_services",
    "industry_delivery_logistics",
    "industry_moving_services",
    "industry_home_healthcare",
  ],
  stock: [
    "industry_gold_jewelry_short",
    "industry_fashion_clothing",
    "industry_pharmacies",
    "industry_grocery_stores",
    "industry_restaurants",
    "industry_wholesale_distribution",
    "industry_hardware_tools",
    "industry_beauty_cosmetics",
  ],
};

const popupIndustryIcons = {
  industry_gold_jewelry: "💍",
  industry_clothing_fashion: "👗",
  industry_pharmacies: "💊",
  industry_grocery_supermarkets: "🛒",
  industry_cosmetics_beauty: "🧴",
  industry_electronics_phones: "📱",
  industry_furniture_home: "🪑",
  industry_laundry_dry_clean: "🧺",
  industry_salons_barbershops: "✂️",
  industry_any_retail_business: "🏪",

  industry_cafes_coffee_shops: "☕",
  industry_dine_in_restaurants: "🍽️",
  industry_fast_food_qsr: "🥙",
  industry_food_courts: "🍕",
  industry_bakeries_desserts: "🧁",
  industry_healthy_food_outlets: "🥗",
  industry_drive_through: "🚗",
  industry_cloud_kitchens: "🍱",

  industry_ac_hvac_repair: "❄️",
  industry_car_wash_detailing: "🚗",
  industry_electrical_services: "🔌",
  industry_plumbing_maintenance: "🔧",
  industry_cleaning_services: "🧹",
  industry_delivery_logistics: "🚚",
  industry_moving_services: "📦",
  industry_home_healthcare: "🏥",

  industry_gold_jewelry_short: "💍",
  industry_fashion_clothing: "👗",
  industry_grocery_stores: "🛒",
  industry_restaurants: "🍔",
  industry_wholesale_distribution: "📦",
  industry_hardware_tools: "🔩",
  industry_beauty_cosmetics: "🧴",
};

const Pricing = ({ callbackSelection }) => {
  const { t } = useTranslation();
  const { appConfig } = useContext(AppContext);

  const isRTL = appConfig?.direction === "rtl";

  const [currentPlan, setCurrentPlan] = useState(null);
  const [plansLoading, setPlansLoading] = useState(true);
  const [usersCount, setUsersCount] = useState(1);
  const [addonsData, setAddonsData] = useState([]);
  const [addonsLoading, setAddonsLoading] = useState(true);
  const [selectedGroups, setSelectedGroups] = useState({
    fnb: false,
    job: false,
    stock: false,
    ticket: false,
  });
  const [activePopup, setActivePopup] = useState(null);

  useEffect(() => {
    let isEffectActive = true;

    getSubscriptionPlansAsObject()
      .then((res) => {
        if (!isEffectActive) return;

        if (res?.data?.success === false) {
          setCurrentPlan(null);
          handleApiError(res, t);
          return;
        }

        const plans = extractApiCollection(res, "subscription_plans");
        setCurrentPlan(getDailySubscriptionPlan(plans));
      })
      .catch((error) => {
        if (isEffectActive) {
          setCurrentPlan(null);
          handleApiError(error, t);
        }
      })
      .finally(() => {
        if (isEffectActive) setPlansLoading(false);
      });

    return () => {
      isEffectActive = false;
    };
  }, [t]);

  useEffect(() => {
    let isEffectActive = true;

    getAllAddons()
      .then((response) => {
        if (!isEffectActive) return;

        if (response?.data?.success === false) {
          setAddonsData([]);
          handleApiError(response, t);
          return;
        }

        setAddonsData(extractApiCollection(response, "addons"));
      })
      .catch((error) => {
        if (isEffectActive) {
          setAddonsData([]);
          handleApiError(error, t);
        }
      })
      .finally(() => {
        if (isEffectActive) setAddonsLoading(false);
      });

    return () => {
      isEffectActive = false;
    };
  }, [t]);

  const planBasePrice = useMemo(() => getNetPrice(currentPlan), [currentPlan]);

  const userDailyPrice = useMemo(
    () => getPlanUserNetPrice(currentPlan),
    [currentPlan],
  );

  const getAddonByName = useCallback(
    (name) => addonsData.find((addon) => addon.name === name) || null,
    [addonsData],
  );

  const getAddonDisplayName = useCallback(
    (addon, fallback = "") => {
      if (!addon) return fallback;
      return (
        (isRTL ? addon?.name_ar : addon?.name) ||
        addon?.name ||
        addon?.name_ar ||
        fallback
      );
    },
    [isRTL],
  );

  const getAddonsFromNames = useCallback(
    (names = []) => {
      return uniqueAddonsById(
        names.map((name) => getAddonByName(name)).filter(Boolean),
      );
    },
    [getAddonByName],
  );

  const groups = useMemo(() => {
    return GROUP_CONFIG.map((group) => {
      const configuredAddons = getAddonsFromNames(group.addonNames || []);
      if (configuredAddons.length !== group.addonNames.length) return null;

      const addons = expandAddonDependencies(
        configuredAddons,
        addonsData,
      );

      return {
        ...group,
        addons,
        displayPrice: sumAddonPrices(addons),
      };
    }).filter(Boolean);
  }, [addonsData, getAddonsFromNames]);

  const trialInfo = useMemo(() => {
    const eligibleAddons = uniqueAddonsById(
      groups
        .flatMap((group) => group.addons || [])
        .filter((addon) => Number(addon?.total_trial_days) > 0),
    );
    const trialDays = eligibleAddons.length
      ? Math.min(
          ...eligibleAddons.map((addon) =>
            Math.max(Math.floor(Number(addon.total_trial_days) || 0), 0),
          ),
        )
      : 0;
    const names = eligibleAddons
      .map((addon) => getAddonDisplayName(addon))
      .filter(Boolean);

    let addonsLabel = names.join(", ");
    try {
      addonsLabel = new Intl.ListFormat(isRTL ? "ar" : "en", {
        style: "long",
        type: "conjunction",
      }).format(names);
    } catch {}

    return {
      addonsLabel,
      days: trialDays,
    };
  }, [getAddonDisplayName, groups, isRTL]);

  const activeGroups = useMemo(
    () => groups.filter((group) => selectedGroups[group.key]),
    [groups, selectedGroups],
  );

  const selectedAddons = useMemo(() => {
    return uniqueAddonsById(
      activeGroups.flatMap((group) => group.addons || []),
    );
  }, [activeGroups]);

  const totalDaily = useMemo(() => {
    return calculatePricingTotal({
      plan: currentPlan,
      usersCount,
      addons: selectedAddons,
    });
  }, [currentPlan, usersCount, selectedAddons]);

  const pricingLoading = plansLoading || addonsLoading;
  const pricingReady = !pricingLoading && Boolean(currentPlan);

  const posIncludedAddons = useMemo(() => {
    return getAddonsFromNames(POS_INCLUDED_ADDON_NAMES);
  }, [getAddonsFromNames]);

  const popupData = useMemo(() => {
    return {
      pos: {
        eyebrow: t("popup_pos_eyebrow"),
        title: t("popup_pos_title"),
        desc: t("popup_pos_desc"),
        industries: (popupIndustryKeyMap.pos || []).map((key) => ({
          key,
          label: t(key),
          icon: popupIndustryIcons[key] || "•",
        })),
      },
      fnb: {
        eyebrow: t("pricing_group_fnb_title"),
        title: t("popup_fnb_title"),
        desc: t("popup_fnb_desc"),
        industries: (popupIndustryKeyMap.fnb || []).map((key) => ({
          key,
          label: t(key),
          icon: popupIndustryIcons[key] || "•",
        })),
      },
      job: {
        eyebrow: t("pricing_group_job_title"),
        title: t("popup_job_title"),
        desc: t("popup_job_desc"),
        industries: (popupIndustryKeyMap.job || []).map((key) => ({
          key,
          label: t(key),
          icon: popupIndustryIcons[key] || "•",
        })),
      },
      stock: {
        eyebrow: t("pricing_group_stock_title"),
        title: t("popup_stock_title"),
        desc: t("popup_stock_desc"),
        industries: (popupIndustryKeyMap.stock || []).map((key) => ({
          key,
          label: t(key),
          icon: popupIndustryIcons[key] || "•",
        })),
      },
    };
  }, [t]);

  const toggleGroup = (groupKey) => {
    const group = GROUP_CONFIG.find((item) => item.key === groupKey);
    if (!group) return;
    setSelectedGroups((prev) => {
      const next = { ...prev, [groupKey]: !prev[groupKey] };
      if (!prev[groupKey] && group.conflictKey) {
        next[group.conflictKey] = false;
      }
      return next;
    });
  };

  const handleContinue = () => {
    if (!pricingReady) return;
    callbackSelection(currentPlan, usersCount, selectedAddons);
  };

  const popup = activePopup ? popupData[activePopup] : null;

  return (
    <>
      <div
        className={`pricing_html_wrapper ${isRTL ? "pricing_rtl" : ""}`}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <div className="pricing_html_hero">
          {trialInfo.days > 0 ? (
            <div className="pricing_html_badge">
              ✦ {t("pricing_badge", { days: trialInfo.days })}
            </div>
          ) : null}

          <h1>
            {t("simple_pricing")}
            <br />
            <span>{t("pay_only_for_what_you_use")}</span>
          </h1>

          <p>{t("pricing_hero_desc")}</p>
        </div>

        <div className="pricing_html_main">
          <div className="pricing_html_left">
            {trialInfo.days > 0 ? (
              <div className="pricing_trial_banner">
                <div className="pricing_trial_icon">🎁</div>
                <div className="pricing_trial_text">
                  <strong>
                    {t("pricing_free_trial_included", {
                      days: trialInfo.days,
                    })}
                  </strong>
                  <span>
                    {t("pricing_trial_desc", {
                      addons: trialInfo.addonsLabel,
                      days: trialInfo.days,
                    })}
                  </span>
                </div>
              </div>
            ) : null}

            {/* ── USERS SECTION ── */}
            <div className="pricing_html_section">
              <div className="pricing_html_section_title">
                {t("how_many_users")}
              </div>

              <div className="pricing_bundle_card pricing_users_bundle">
                <div className="pricing_bundle_header pricing_bundle_header_static">
                  <div className="pricing_bundle_icon brand">👤</div>

                  <div className="pricing_bundle_info">
                    <div className="pricing_bundle_name">{t("pos_users")}</div>
                    <div className="pricing_bundle_desc">
                      {t("pricing_users_desc", {
                        amount: userDailyPrice.toFixed(2),
                      })}
                    </div>
                  </div>

                  <div className="pricing_bundle_price_wrap pricing_users_price_wrap">
                    <QuantityStepper
                      value={usersCount}
                      min={1}
                      onDecrease={() =>
                        setUsersCount((prev) => (prev > 1 ? prev - 1 : 1))
                      }
                      onIncrease={() => setUsersCount((prev) => prev + 1)}
                    />

                    <div className="pricing_bundle_price">
                      <div className="pricing_bundle_price_num">
                        <CurrencyFormatter
                          value={userDailyPrice * usersCount}
                        />
                      </div>
                      <div className="Per_day_label">{t("per_day")}</div>
                    </div>
                  </div>
                </div>

                <div className="pricing_bundle_sub pricing_bundle_sub_always_open">
                  <div className="pricing_sub_label">
                    {t("included_in_every_plan")}
                  </div>

                  <div className="pricing_sub_modules">
                    <div className="pricing_sub_module">
                      <span className="pricing_sub_module_dot" />
                      {t("point_of_sale")}
                    </div>
                    <div className="pricing_sub_module">
                      <span className="pricing_sub_module_dot" />
                      {t("zatca_invoicing")}
                    </div>
                    <div className="pricing_sub_module">
                      <span className="pricing_sub_module_dot" />
                      {t("customer_database")}
                    </div>
                    <div className="pricing_sub_module">
                      <span className="pricing_sub_module_dot" />
                      {t("multi_branch")}
                    </div>
                    <div className="pricing_sub_module">
                      <span className="pricing_sub_module_dot" />
                      {t("products_catalogue")}
                    </div>
                    <div className="pricing_sub_module">
                      <span className="pricing_sub_module_dot" />
                      {t("sales_reports")}
                    </div>

                    {posIncludedAddons.map((addon) => (
                      <div key={addon.id} className="pricing_sub_module">
                        <span className="pricing_sub_module_dot" />
                        {getAddonDisplayName(addon)}
                      </div>
                    ))}

                    <Button
                      type="text"
                      htmlType="button"
                      className="usecase_btn_inline"
                      onClick={() => setActivePopup("pos")}
                    >
                      {t("business_types")} ↗
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* ── MODULES SECTION ── */}
            <div className="pricing_html_section">
              <div className="pricing_html_section_title">
                {t("choose_your_modules")}
              </div>

              {groups.map((group) => {
                const isSelected = selectedGroups[group.key];

                return (
                  <div
                    key={group.key}
                    className={`pricing_bundle_card ${isSelected ? "selected" : ""}`}
                  >
                    <div
                      className="pricing_bundle_header"
                      onClick={() => toggleGroup(group.key)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleGroup(group.key);
                        }
                      }}
                    >
                      <div
                        className={`pricing_bundle_icon ${group.colorClass}`}
                      >
                        {group.icon}
                      </div>

                      <div className="pricing_bundle_info">
                        <div className="pricing_bundle_name">
                          {t(group.titleKey)}
                        </div>
                        <div className="pricing_bundle_desc">
                          {t(group.descKey)}
                        </div>
                      </div>

                      <div className="pricing_bundle_price_wrap">
                        <div className="pricing_bundle_price">
                          <div className="pricing_bundle_price_num">
                            <CurrencyFormatter value={group.displayPrice} />
                          </div>
                          <div className="Per_day_label">{t("per_day")}</div>
                        </div>

                        <button
                          type="button"
                          className={`pricing_bundle_toggle ${isSelected ? "on" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleGroup(group.key);
                          }}
                        />
                      </div>
                    </div>

                    {isSelected && group.addons.length > 0 ? (
                      <div className="pricing_bundle_sub">
                        <div className="pricing_sub_label">
                          {t("included_in_this_group")}
                        </div>

                        <div className="pricing_sub_modules">
                          {group.addons.map((addon) => (
                            <div
                              key={addon.id}
                              className="pricing_sub_module"
                            >
                              <span className="pricing_sub_module_dot" />
                              {getAddonDisplayName(addon)}
                            </div>
                          ))}

                          {group.popupKey ? (
                            <Button
                              type="text"
                              htmlType="button"
                              className="usecase_btn_inline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActivePopup(group.popupKey);
                              }}
                            >
                              {t("business_types")} ↗
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── RIGHT SUMMARY PANEL ── */}
          <div className="pricing_html_right">
            <div className="pricing_summary_card">
              <div className="pricing_summary_header">
                <div className="your_plan_summary">
                  {t("your_plan_summary")}
                </div>
                <div className="pricing_summary_total_label">
                  {t("daily_total")}
                </div>
                <div className="pricing_summary_total">
                  <CurrencyFormatter
                    value={totalDaily}
                    numbercolor="var(--color-white)"
                  />
                </div>
                {trialInfo.days > 0 ? (
                  <div className="pricing_summary_total_sub">
                    {t("pricing_after_trial_note", {
                      days: trialInfo.days,
                    })}
                  </div>
                ) : null}
              </div>

              <div className="pricing_summary_body">
                {!plansLoading && !currentPlan ? (
                  <Alert
                    type="error"
                    showIcon
                    message={t("daily_subscription_plan_unavailable")}
                    className="app-mb-20"
                  />
                ) : null}

                {planBasePrice > 0 ? (
                  <div className="pricing_summary_row">
                    <span className="pricing_sr_label">
                      {currentPlan?.name || t("subscription_plan")}
                    </span>
                    <span className="pricing_sr_val">
                      <CurrencyFormatter value={planBasePrice} />
                    </span>
                  </div>
                ) : null}

                <div className="pricing_summary_row">
                  <span className="pricing_sr_label">
                    {usersCount} {t("user")} ×{" "}
                    <CurrencyFormatter value={userDailyPrice} />
                  </span>
                  <span className="pricing_sr_val">
                    <CurrencyFormatter value={userDailyPrice * usersCount} />
                  </span>
                </div>

                {activeGroups.map((group) => (
                  <div key={group.key}>
                    <div className="pricing_summary_row">
                      <span className="pricing_sr_label">
                        {t(group.titleKey)}
                      </span>
                      <span className="pricing_sr_val">
                        <CurrencyFormatter value={group.displayPrice} />
                      </span>
                    </div>
                  </div>
                ))}

                <div className="pricing_summary_row">
                  <span className="pricing_sr_label pricing_sr_total_label">
                    {t("daily_total")}
                  </span>
                  <span className="pricing_sr_val pricing_sr_total">
                    <CurrencyFormatter value={totalDaily} />
                  </span>
                </div>

                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={handleContinue}
                  loading={pricingLoading}
                  disabled={!pricingReady}
                >
                  {t("start_free_trial")} {isRTL ? "←" : "→"}
                </Button>

                <div className="pricing_cta_note">
                  {trialInfo.days > 0 ? (
                    <>
                      {t("pricing_cta_line_1", { days: trialInfo.days })}
                      <br />
                    </>
                  ) : null}
                  {t("pricing_cta_line_2", {
                    amount: totalDaily.toFixed(2),
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── USE-CASE POPUP — Ant Design Modal ── */}
      <Modal
        open={!!activePopup}
        onCancel={() => setActivePopup(null)}
        footer={null}
        centered
        width={580}
        closeIcon={
          <span className="pricing_popup_close_icon">
            <CloseOutlined />
          </span>
        }
        className={`pricing_popup_modal${isRTL ? " pricing_popup_modal_rtl" : ""}`}
        styles={{
          mask: {
            backdropFilter: "blur(4px)",
            background: "var(--color-shadow-soft)",
          },
        }}
      >
        {popup && (
          <div className="pricing_popup_body">
            <div className="popup_header">
              <div>
                <div className="popup_eyebrow">{popup.eyebrow}</div>
                <div className="popup_title">{popup.title}</div>
              </div>
            </div>

            <p className="popup_desc">{popup.desc}</p>

            <div className="popup_industries">
              {popup.industries.map((item) => (
                <div key={item.key} className="popup_industry">
                  <span className="popup_industry_icon">{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default Pricing;
